import { useEffect, useState } from 'react';
import { fetchTickets } from '../api';
import { isOwner } from '../owner';
export type Mode = 'home' | 'market' | 'mystatus' | 'watchlist' | 'scanner' | 'daytrade' | 'newday' | 'stats' | 'themes' | 'radar' | 'backtest';

const BASE_TABS: { id: Mode; label: string }[] = [
  { id: 'home', label: '홈' },
  { id: 'market', label: '시장' },
  { id: 'mystatus', label: '내 현황' },
  { id: 'watchlist', label: '관심종목' },
  { id: 'scanner', label: '전체 스캔' },
  { id: 'daytrade', label: '단타' },
  { id: 'themes', label: '주도 테마' },
  { id: 'radar', label: '글로벌 레이더' },
  { id: 'backtest', label: '백테스트' },
];

// 로고 + 탭 + 날짜를 한 줄로 통합한 상단 바.
// 🎨 규격 V2(08-23): 활성 탭은 알약 배경이 아니라 **글자 강조 + 앰버 밑줄 2px**. 구획은 헤어라인 1px.
export default function Header({ mode, onTab }: { mode: Mode; onTab: (m: Mode) => void }) {
  const dateStr = new Date().toLocaleDateString('ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  });

  // 🎫 매매 제안서 이펙트(관리자 전용, 07-10 → 07-16 뉴단타 해체로 '단타' 탭에 부착) — 다른 탭 보는 중에도 매매 제안서를 놓치지 않게:
  //   live=진행 중 매매 제안서(호박색 펄스+핑 점) · unseen=안 본 새 결과(정지 점). 단타 탭 방문 시 본 것으로 처리.
  const [tkSig, setTkSig] = useState<'live' | 'unseen' | null>(null);
  useEffect(() => {
    if (!isOwner()) return;
    let alive = true;
    const poll = () => fetchTickets().then((d) => {
      if (!alive) return;
      const latest = Math.max(0, ...(d.today || []).map((t) => t.ts || 0));
      const seen = Number(localStorage.getItem('nd_seen_ts') || 0);
      if (mode === 'daytrade') {   // 보고 있는 중 — 최신까지 봤다고 기록하고 이펙트 끔
        if (latest > seen) localStorage.setItem('nd_seen_ts', String(latest));
        setTkSig(null);
        return;
      }
      setTkSig(d.active ? 'live' : latest > seen ? 'unseen' : null);
    }).catch(() => { /* noop */ });
    poll();
    const i = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(i); };
  }, [mode]);

  return (
    <div className="relative flex items-center gap-2 sm:gap-3 px-3 sm:px-4 border-b border-dc-hair shrink-0">
      {/* 로고 — 클릭 시 홈으로 */}
      <button onClick={() => onTab('home')} className="flex items-center gap-2 shrink-0 py-3 pr-1.5" title="홈으로">
        <img src="/logo192.png?v=2" alt="줍줍" className="w-[26px] h-[26px] rounded-lg" />
        <span className="text-dc-ink1 text-sm font-bold tracking-tight whitespace-nowrap hidden sm:inline">줍줍</span>
      </button>

      {/* 탭 — 좁은 화면에선 가로 스크롤 */}
      <nav className="flex items-center gap-0.5 overflow-x-auto flex-1 min-w-0 no-scrollbar">
        {[...BASE_TABS, ...(isOwner() ? [{ id: 'stats' as Mode, label: '📊 통계' }] : [])].map((t) => (
          <button
            key={t.id}
            onClick={() => onTab(t.id)}
            className={`relative shrink-0 px-2.5 py-1.5 text-[13px] border-b-2 transition-colors ${
              mode === t.id
                ? 'font-bold text-dc-ink1 border-dc-point'
                : t.id === 'daytrade' && tkSig === 'live'
                ? 'font-semibold text-dc-point border-dc-point/40 animate-pulse'
                : t.id === 'daytrade' && tkSig === 'unseen'
                ? 'font-medium text-dc-point/80 border-transparent hover:text-dc-point'
                : 'font-medium text-dc-ink3 border-transparent hover:text-dc-ink1'
            }`}
            title={t.id === 'daytrade' && tkSig === 'live' ? '🎫 티켓 진행 중!' : t.id === 'daytrade' && tkSig === 'unseen' ? '🎫 안 본 티켓 결과 있음' : undefined}
          >
            {t.label}
            {t.id === 'daytrade' && tkSig && (
              <>
                <span className="absolute top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-dc-point" />
                {tkSig === 'live' && <span className="absolute top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-dc-point animate-ping" />}
              </>
            )}
          </button>
        ))}
      </nav>

      {/* 날짜 — 넓은 화면에서만 */}
      <span className="text-dc-ink3 text-[11.5px] shrink-0 whitespace-nowrap hidden md:block">{dateStr}</span>

      {/* 폰 스크롤 힌트 — 탭이 더 있다는 걸 알려주는 우측 페이드(스크롤바가 숨겨져 있어 단서가 없었다) */}
      <div className="pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-[#0B0F17] to-transparent md:hidden" />
    </div>
  );
}
